/**
 * Location utilities for South African provinces, metros, and neighborhoods.
 * Uses african-states-and-tribes package for official province data.
 */

import { getCountryAndState } from 'african-states-and-tribes';

interface StateData {
  name: string;
  stateCode: string;
  subdivisions?: string[];
}

export interface Province {
  name: string;
  code: string;
  subdivisions: string[];
}

export interface NeighborhoodsByMetro {
  [metro: string]: string[];
}

// Cache for province data
let provinceDataCache: Province[] | null = null;

/**
 * Get all South African provinces with their subdivisions (metros/districts).
 */
export async function getProvinces(): Promise<Province[]> {
  if (provinceDataCache) {
    return provinceDataCache;
  }

  try {
    const southAfrica: any = await getCountryAndState('ZA');
    
    provinceDataCache = ((southAfrica.states || []) as StateData[]).map((state: StateData) => ({
      name: state.name,
      code: state.stateCode,
      subdivisions: state.subdivisions || [],
    }));

    return provinceDataCache || [];
  } catch (error) {
    console.error('Error loading province data:', error);
    return [];
  }
}

/**
 * Get province name from code (e.g., "WC" -> "Western Cape").
 */
export async function getProvinceName(code: string): Promise<string | null> {
  const provinces = await getProvinces();
  const province = provinces.find((p) => p.code === code);
  return province ? province.name : null;
}

/**
 * Get metros/districts for a specific province.
 */
export async function getMetrosForProvince(provinceCode: string): Promise<string[]> {
  const provinces = await getProvinces();
  const province = provinces.find((p) => p.code === provinceCode);
  return province ? province.subdivisions : [];
}

/**
 * Structured neighborhood data organized by metro/city.
 * This is curated data for popular South African cities.
 */
export const neighborhoodsByMetro: NeighborhoodsByMetro = {
  // Western Cape - Cape Town
  'City of Cape Town': [
    'Sea Point',
    'Gardens',
    'Woodstock',
    'Observatory',
    'Claremont',
    'Rondebosch',
    'Green Point',
    'City Bowl',
    'Camps Bay',
    'Constantia',
    'Newlands',
    'Muizenberg',
    'Kenilworth',
    'Mowbray',
    'Salt River',
    'Tamboerskloof',
  ],

  // Gauteng - Johannesburg
  'City of Johannesburg': [
    'Sandton',
    'Rosebank',
    'Braamfontein',
    'Melville',
    'Parktown',
    'Randburg',
    'Fourways',
    'Northcliff',
    'Greenside',
    'Parkhurst',
    'Illovo',
    'Hyde Park',
    'Johannesburg CBD',
  ],

  // Gauteng - Pretoria/Tshwane
  'City of Tshwane': [
    'Hatfield',
    'Brooklyn',
    'Menlyn',
    'Centurion',
    'Pretoria CBD',
    'Arcadia',
    'Waterkloof',
    'Garsfontein',
    'Lynnwood',
    'Faerie Glen',
  ],

  // KwaZulu-Natal - Durban
  'eThekwini': [
    'Umhlanga',
    'Morningside',
    'Berea',
    'Durban North',
    'Glenwood',
    'Musgrave',
    'Westville',
    'Ballito',
    'Durban CBD',
    'Florida Road',
  ],
};

/**
 * Get neighborhoods for a specific metro/city.
 */
export function getNeighborhoodsForMetro(metro: string): string[] {
  return neighborhoodsByMetro[metro] || [];
}

/**
 * Get all unique neighborhoods across all metros (for backward compatibility).
 */
export function getAllNeighborhoods(): string[] {
  const allNeighborhoods = new Set<string>();
  
  Object.values(neighborhoodsByMetro).forEach((neighborhoods) => {
    neighborhoods.forEach((n) => allNeighborhoods.add(n));
  });

  return Array.from(allNeighborhoods).sort();
}

/**
 * Find which metro a neighborhood belongs to.
 */
export function getMetroForNeighborhood(neighborhood: string): string | null {
  for (const [metro, neighborhoods] of Object.entries(neighborhoodsByMetro)) {
    if (neighborhoods.includes(neighborhood)) {
      return metro;
    }
  }
  return null;
}

/**
 * Synchronous helper to get province data if already cached.
 * Useful for initial renders before async data loads.
 */
export function getCachedProvinces(): Province[] {
  return provinceDataCache || [];
}

/**
 * Pre-load province data (call this on app initialization).
 */
export async function preloadProvinceData(): Promise<void> {
  await getProvinces();
}
